import type { ForwardRefRenderFunction, KeyboardEventHandler } from 'react';
import React, { useImperativeHandle, forwardRef, useRef } from 'react';
import classNames from 'clsx';

import {
  activateByEnterButton,
  formatClassNames,
} from '@wix/editor-elements-common-utils';
import type {
  ILoginSocialBarCustomMenu,
  ILoginSocialBarProps,
  LoginSocialBarMenuItemProps,
} from '../../../../LoginSocialBar.types';
import { TEST_ID_CUSTOM_MENU, TEST_ID_LINK } from '../../../../constants';
import Link from '@wix/thunderbolt-elements/src/components/Link/viewer/Link';
import {
  BIAction,
  getMembersAreaNavigationEvent,
  MEMBERS_APP_UOU_ENDPOINT,
} from '../../../bi/events';
import styles from './LoginSocialBarCustomMenu.module.scss';
import { createMenuKeyboardNavigationHandler } from '../../../../utils/keyboardUtils';
import semanticClassNames from '../../LoginSocialBar.semanticClassNames';
import { useMenuPositioning } from './hooks';

type ILoginSocialBarCustomMenuProps = Pick<
  ILoginSocialBarProps,
  | 'menuItems'
  | 'onLogout'
  | 'translations'
  | 'currentPrimaryPageHref'
  | 'reportBi'
> & {
  ariaLabel: string;
  open: boolean;
  onClose(): void;
  reportBiOnMenuItemClick: (item: LoginSocialBarMenuItemProps) => void;
  rootElement: HTMLDivElement | null;
  referenceElement: HTMLDivElement | null;
  useNewCustomMenuPositioning: boolean;
};

export const LOGIN_SOCIAL_BAR_CUSTOM_MENU_ID = 'loginSocialBarCustomMenu';

const queryFocusableMenuElements = (menuElement: HTMLElement) =>
  Array.from(menuElement.querySelectorAll<HTMLElement>('[tabindex],[href]'));

const LoginSocialBarCustomMenu: ForwardRefRenderFunction<
  ILoginSocialBarCustomMenu,
  ILoginSocialBarCustomMenuProps
> = (
  {
    ariaLabel,
    menuItems,
    translations,
    currentPrimaryPageHref,
    open,
    rootElement,
    referenceElement,
    useNewCustomMenuPositioning,
    onLogout,
    onClose,
    reportBi,
    reportBiOnMenuItemClick,
  },
  ref,
) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { menuStyles, isVisible } = useMenuPositioning({
    isMenuOpen: open,
    rootElement,
    referenceElement,
    popperElement: containerRef.current,
    useNewCustomMenuPositioning,
  });

  const className = classNames(styles.root, {
    [styles.open]: open,
    [styles.customPositioning]: useNewCustomMenuPositioning,
    // for dynamic positioning - we keep menu hidden until
    // necessary styles are applied, to avoid flickering
    [styles.visible]: isVisible,
  });

  useImperativeHandle(ref, () => ({
    focusFirstMenuItem: () => {
      if (!containerRef.current) {
        return;
      }
      const [firstElement] = queryFocusableMenuElements(containerRef.current);
      if (firstElement) {
        firstElement.focus();
      }
    },
  }));

  const handleKeyDown: KeyboardEventHandler =
    createMenuKeyboardNavigationHandler(onClose, containerRef);

  const onLinkClick = (item: LoginSocialBarMenuItemProps) => {
    reportBiOnMenuItemClick(item);
    void reportBi(
      getMembersAreaNavigationEvent({
        action: BIAction.ItemClick,
        pageName: item.label,
      }),
      {
        endpoint: MEMBERS_APP_UOU_ENDPOINT,
      },
    );
  };

  const handleLogout = () => {
    reportBiOnMenuItemClick({ label: translations.logOutText!, link: {} });
    onLogout();
  };

  return (
    <div
      id={LOGIN_SOCIAL_BAR_CUSTOM_MENU_ID}
      ref={containerRef}
      className={classNames(
        formatClassNames(semanticClassNames.dropdownMenu),
        semanticClassNames.dropdownMenu,
        className,
      )}
      aria-label={ariaLabel}
      role="menu"
      data-testid={TEST_ID_CUSTOM_MENU}
      onKeyDown={handleKeyDown}
      onClick={onClose}
      style={menuStyles}
    >
      <ul className={styles.menuList}>
        {menuItems.map((item, index) => (
          <li key={index}>
            <Link
              role="menuitem"
              onClick={() => onLinkClick(item)}
              {...item.link}
              dataTestId={TEST_ID_LINK}
              className={classNames(styles.link, {
                [styles.selected]:
                  !!item.link.href && item.link.href === currentPrimaryPageHref,
              })}
            >
              <span className={styles.label}>{item.label}</span>
              {typeof item.displayCount === 'number' && (
                <span className={styles.displayCount}>
                  ({item.displayCount})
                </span>
              )}
            </Link>
          </li>
        ))}
        {!!menuItems.length && (
          <li role="separator">
            <hr />
          </li>
        )}
        <li>
          <div
            role="menuitem"
            className={styles.link}
            onClick={handleLogout}
            tabIndex={0}
            data-testid={TEST_ID_LINK}
            onKeyDown={activateByEnterButton}
          >
            <span className={styles.label}>{translations.logOutText}</span>
          </div>
        </li>
      </ul>
    </div>
  );
};

export default forwardRef(LoginSocialBarCustomMenu);
